import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import github1 from '../../../assets/Deploy/github1.png'
import PageNavigation from '../../../components/PageNavigation/PageNavigation'
import github3 from '../../../assets/Deploy/github3.png'
import github4 from '../../../assets/Deploy/github4.png'
import github5 from '../../../assets/Deploy/github5.png'
import image1 from '../../../assets/Deploy/image1.png'
import image2 from '../../../assets/Deploy/image2.png'
import image3 from '../../../assets/Deploy/image3.png'
import image4 from '../../../assets/Deploy/image4.png'
import './Apps.css'

const Apps = () => {
  const [activeStep, setActiveStep] = useState(1)
  const [lightboxImage, setLightboxImage] = useState(null)
  const [lightboxAlt, setLightboxAlt] = useState('')

  const openLightbox = (imageSrc, altText) => {
    setLightboxImage(imageSrc)
    setLightboxAlt(altText)
    document.body.style.overflow = 'hidden' // Prevent background scrolling
  }

  const closeLightbox = () => {
    setLightboxImage(null)
    setLightboxAlt('')
    document.body.style.overflow = '' // Restore scrolling
  }

  // Close lightbox on ESC key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && lightboxImage) {
        closeLightbox()
      }
    }
    if (lightboxImage) {
      window.addEventListener('keydown', handleEscape)
    }
    return () => {
      window.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = '' // Cleanup: restore scrolling
    }
  }, [lightboxImage])

  const steps = [
    {
      id: 1,
      title: 'Choose Your Deployment Method',
      description: 'Aedify.AI offers two ways to deploy from GitHub. Choose the method that works best for you.',
      details: 'You can either paste a public GitHub repository URL directly, or connect your own GitHub account to access your private repositories and enable automatic deployments.',
      image: github1,
      hasTwoOptions: true,
      optionA: {
        title: 'Option A: Use External GitHub Link',
        description: 'Deploy any public GitHub repository by pasting its URL.',
        details: 'Simply paste the GitHub repository URL in the search bar (e.g., https://github.com/username/repo-name). This method works for public repositories and doesn\'t require connecting your GitHub account.'
      },
      optionB: {
        title: 'Option B: Connect Your GitHub Account',
        description: 'Connect your GitHub account to access your repositories and enable automatic deployments.',
        details: 'Click the "Connect" button on the GitHub card to authorize Aedify.AI. This allows you to access your private repositories and enables automatic deployments when you push changes to your repository.'
      }
    },
    {
      id: 2,
      title: 'Select or Enter Repository',
      description: 'Choose the repository you want to deploy. Aedify.AI automatically detects your application setup.',
      image: github3,
      details: 'If you connected your GitHub account, browse and select from your available repositories. If using an external link, paste the repository URL in the search bar and click "Continue".',
      hasDetectionInfo: true,
      detectionInfo: {
        framework: 'Aedify.AI automatically detects your framework (React, Next.js, SvelteKit, etc.) and configures the build settings accordingly. The detected framework is shown in the project details.',
        docker: 'If your repository contains a Dockerfile, you can choose to use it directly for deployment. Simply check the "Use Dockerfile" option in the DETECTION section. This allows you to have full control over the build process.'
      }
    },
    {
      id: 3,
      title: 'Configure Project Settings and Resources',
      description: 'Set your project name, environment variables, CPU, and memory limits.',
      image: github4,
      hasConfigurationInfo: true,
      details: 'Configure all your project settings in one place. This includes your project name, environment variables, and resource allocation.',
      configurationInfo: {
        environmentVariables: 'Add environment variables your application needs. You can add multiple key-value pairs using the "Add More" button, or import them all at once using the "Import .env" option.',
        resources: 'Use the CPU and Memory sliders to set your resource allocation. Start with minimal resources (0.1 or 0.25 vCPU, 128MB or 256MB) for testing, then scale up as needed. The maximum available depends on your plan. You can adjust these limits later.',
        autoDeploy: 'Enable automatic deployment to automatically rebuild and redeploy your application whenever you push changes to your GitHub repository.'
      }
    },
    {
      id: 4,
      title: 'Deploy Application',
      description: 'Review all your settings, including the estimated monthly cost shown at the bottom. When ready, click "Deploy Project" to start the build and deployment process.',
      image: github5,
      details: 'Aedify.AI will build your application from the repository and deploy it. You can monitor the build progress in real-time through the deployment logs. You can view detailed build logs and runtime logs.'
    }
  ]

  const deployFromImageSteps = [
    {
      id: 1,
      title: 'Choose Your Image Source',
      description: 'Select where you want to deploy your container image from.',
      image: image1,
      hasTwoOptions: true,
      details: 'Aedify.AI offers two ways to deploy container images. Choose the method that works best for you.',
      optionA: {
        title: 'Using Aedify Registry',
        description: 'Deploy images from your Aedify.AI container registry.',
        image: image1,
        details: 'Use images stored in your Aedify.AI registry. If you haven\'t created any images yet, you can push images to the registry first. Learn how to create and manage images in the Registry documentation.',
        registryLink: '/platform-capabilities/registry'
      },
      optionB: {
        title: 'Using Own Images',
        description: 'Deploy images from external registries.',
        image: image2,
        details: 'Enter the full image URL from any container registry (e.g., docker.io/username/app:tag or ghcr.io/username/app:latest). This feature is coming soon.',
        comingSoon: true
      }
    },
    {
      id: 2,
      title: 'Select or Enter Image',
      description: 'Choose the image you want to deploy or enter an image URL.',
      image: image3,
      hasImageRight: true,
      details: 'If using Aedify Registry, browse and select from your available images. The registry shows your repositories with tags. Click on a repository to expand and see its tags, then select the specific tag you want to deploy. A confirmation message will show your selected image.'
    },
    {
      id: 3,
      title: 'Configure Project Settings and Resources',
      description: 'Set your project name, exposed ports, environment variables, CPU, and memory limits.',
      image: image4,
      hasConfigurationInfo: true,
      details: 'Configure all your project settings in one place. This includes your project name, exposed ports, environment variables, and resource allocation.',
      configurationInfo: {
        exposedPorts: 'Add the ports your container application listens on. Click "Add Port" to add port if your application requires it.',
        environmentVariables: 'Add environment variables your application needs. You can add multiple key-value pairs using the "Add More" button, or import them all at once using the "Import .env" option.',
        resources: 'Use the CPU and Memory sliders to set your resource allocation. Start with minimal resources (0.1 or 0.25 vCPU, 128MB or 256MB) for testing, then scale up as needed. The maximum available depends on your plan. You can adjust these limits later.'
      }
    }
  ]

  // Shared steps for both GitHub and Image deployments (step 4 only)
  const sharedDeploymentSteps = [
    {
      id: 4,
      title: 'Deploy Application',
      description: 'Review all your settings, including the estimated monthly cost shown at the bottom. When ready, click "Deploy Project" to start the build and deployment process.',
      image: github5,
      details: 'Aedify.AI will build your application from the repository and deploy it. You can monitor the build progress in real-time through the deployment logs. You can view detailed build logs and runtime logs.'
    }
  ]

  useEffect(() => {
    const handleScroll = () => {
      const allStepIds = [
        ...steps.map(s => ({ id: `github-step-${s.id}`, stepId: s.id + 10 })),
        ...deployFromImageSteps.map(s => ({ id: `image-step-${s.id}`, stepId: s.id + 20 })),
        ...sharedDeploymentSteps.map(s => ({ id: `shared-step-${s.id}`, stepId: s.id + 30 }))
      ]
      const scrollPosition = window.scrollY + 200

      for (let i = allStepIds.length - 1; i >= 0; i--) {
        const element = document.getElementById(allStepIds[i].id)
        if (element && element.offsetTop <= scrollPosition) {
          setActiveStep(allStepIds[i].stepId)
          break
        }
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll() // Initial check
    return () => window.removeEventListener('scroll', handleScroll)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="apps-page">
      <div className="apps-content-wrapper">
        <div className="apps-main-content">
          <h1>Apps</h1>
          <p className="intro">
            Deploy and manage your applications on Aedify.AI. You can deploy from GitHub repositories 
            or directly from container images. If you're new to Aedify.AI, start with the{' '}
            <Link to="/quick-start" className="inline-link">Quick Start guide</Link> to set up your account first.
                      </p>

          {/* Deploy from GitHub Section */}
          <section className="deployment-section">
            <h2>Deploy from GitHub</h2>
            <p className="section-intro">
              Deploy applications directly from GitHub repositories. You can use a public repository URL or connect your GitHub account to access private repositories and enable automatic deployments. 
              Aedify.AI automatically detects your application stack and configures the build process.
            </p>

            {steps.map((step, index) => (
              <div 
                key={step.id} 
                className={`step-container ${activeStep === step.id + 10 ? 'active' : ''}`}
                id={`github-step-${step.id}`}
              >
                <div className="step-header">
                  <div className="step-number">{step.id}</div>
                  <h3>{step.title}</h3>
                </div>
                <p className="step-description">{step.description}</p>
                {step.hasTwoOptions ? (
                  <div className="step-options">
                    {step.image && (
                      <div className="step-image">
                        <img 
                          src={step.image} 
                          alt={`Step ${step.id}: ${step.title}`}
                          onClick={() => openLightbox(step.image, `Step ${step.id}: ${step.title}`)}
                          className="clickable-image"
                        />
                      </div>
                    )}
                    <div className="step-details">
                      <p>{step.details}</p>
                    </div>
                    <div className="options-container">
                      <div className="option-card">
                        <div className="option-header">
                          <span className="option-label">Option A</span>
                          <h4>{step.optionA.title}</h4>
                        </div>
                        <p className="option-description">{step.optionA.description}</p>
                        <div className="option-details">
                          <p>{step.optionA.details}</p>
                        </div>
                      </div>
                      <div className="option-card">
                        <div className="option-header">
                          <span className="option-label">Option B</span>
                          <h4>{step.optionB.title}</h4>
                        </div>
                        <p className="option-description">{step.optionB.description}</p>
                        <div className="option-details">
                          <p>{step.optionB.details}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : step.hasDetectionInfo ? (
                  <div className="step-detection-content">
                    <div className="detection-details">
                      <div className="step-details">
                        <p>{step.details}</p>
                      </div>
                      <div className="detection-section">
                        <h4 className="detection-title">Automatic Detection</h4>
                        <div className="detection-item">
                          <div className="detection-text">
                            <strong>Framework Detection</strong>
                            <p>{step.detectionInfo.framework}</p>
                          </div>
                        </div>
                        <div className="detection-item">
                          <div className="detection-text">
                            <strong>Docker Detection</strong>
                            <p>{step.detectionInfo.docker}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    {step.image && (
                      <div className="detection-image-wrapper">
                        <div className="step-image detection-image">
                          <img 
                            src={step.image} 
                            alt={`Step ${step.id}: ${step.title}`}
                            onClick={() => openLightbox(step.image, `Step ${step.id}: ${step.title}`)}
                            className="clickable-image"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ) : step.hasConfigurationInfo ? (
                  <div className="step-configuration-content">
                    {step.image && (
                      <div className="configuration-image-wrapper">
                        <div className="step-image configuration-image">
                          <img 
                            src={step.image} 
                            alt={`Step ${step.id}: ${step.title}`}
                            onClick={() => openLightbox(step.image, `Step ${step.id}: ${step.title}`)}
                            className="clickable-image"
                          />
                        </div>
                      </div>
                    )}
                    <div className="configuration-details">
                      <div className="configuration-section">
                        <h4 className="configuration-title">Configuration Options</h4>
                        <div className="configuration-item">
                          <div className="configuration-text">
                            <strong>Environment Variables</strong>
                            <p>{step.configurationInfo.environmentVariables}</p>
                          </div>
                        </div>
                        <div className="configuration-item">
                          <div className="configuration-text">
                            <strong>CPU & Memory Allocation</strong>
                            <p>{step.configurationInfo.resources}</p>
                            <p>
                        For more information, see{' '}
                        <a href="https://aedify.ai/pricing" target="_blank" rel="noopener noreferrer" className="pricing-link">Aedify pricing</a>.
                      </p>
                          </div>
                        </div>
                        <div className="configuration-item">
                          <div className="configuration-text">
                            <strong>Auto-Deploy</strong>
                            <p>{step.configurationInfo.autoDeploy}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {step.image && (
                      <div className="step-image">
                        <img 
                          src={step.image} 
                          alt={`Step ${step.id}: ${step.title}`}
                          onClick={() => openLightbox(step.image, `Step ${step.id}: ${step.title}`)}
                          className="clickable-image"
                        />
                      </div>
                    )}
                    <div className="step-details">
                      <p>{step.details}</p>
                    </div>
                  </>
                )}
              </div>
            ))}
          </section>

          {/* Deploy from Image Section */}
          <section className="deployment-section">
            <h2>Deploy from Image</h2>
            <p className="section-intro">
              Deploy pre-built container images from any container registry, including Docker Hub, 
              GitHub Container Registry, or your own private registry.
            </p>

            {deployFromImageSteps.map((step) => (
              <div 
                key={step.id} 
                className={`step-container ${activeStep === step.id + 20 ? 'active' : ''}`}
                id={`image-step-${step.id}`}
              >
                <div className="step-header">
                  <div className="step-number">{step.id}</div>
                  <h3>{step.title}</h3>
                </div>
                <p className="step-description">{step.description}</p>
                {step.hasTwoOptions ? (
                  <div className="step-options">
                    <div className="step-details">
                      <p>{step.details}</p>
                    </div>
                    <div className="options-container">
                      <div className="option-card">
                        <div className="option-header">
                          <span className="option-label">Option A</span>
                          <h4>{step.optionA.title}</h4>
                        </div>
                        {step.optionA.image && (
                          <div className="option-image">
                            <img 
                              src={step.optionA.image} 
                              alt={step.optionA.title}
                              onClick={() => openLightbox(step.optionA.image, step.optionA.title)}
                              className="clickable-image"
                            />
                          </div>
                        )}
                        <p className="option-description">{step.optionA.description}</p>
                        <div className="option-details">
                          <p>{step.optionA.details}</p>
                          {step.optionA.registryLink && (
                            <p>
                              <Link to={step.optionA.registryLink} className="registry-link">
                                Learn more about Registry →
                              </Link>
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="option-card">
                        <div className="option-header">
                          <span className="option-label">Option B</span>
                          <h4>{step.optionB.title}</h4>
                          {step.optionB.comingSoon && (
                            <span className="coming-soon-badge">Coming Soon</span>
                          )}
                        </div>
                        {step.optionB.image && (
                          <div className="option-image">
                            <img 
                              src={step.optionB.image} 
                              alt={step.optionB.title}
                              onClick={() => openLightbox(step.optionB.image, step.optionB.title)}
                              className="clickable-image"
                            />
                          </div>
                        )}
                        <p className="option-description">{step.optionB.description}</p>
                        <div className="option-details">
                          <p>{step.optionB.details}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : step.hasImageRight ? (
                  <div className="step-image-right-content">
                    <div className="image-right-details">
                      <div className="step-details">
                        <p>{step.details}</p>
                      </div>
                    </div>
                    {step.image && (
                      <div className="image-right-wrapper">
                        <div className="step-image image-right-image">
                          <img 
                            src={step.image} 
                            alt={`Step ${step.id}: ${step.title}`}
                            onClick={() => openLightbox(step.image, `Step ${step.id}: ${step.title}`)}
                            className="clickable-image"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ) : step.hasConfigurationInfo ? (
                  <div className="step-configuration-content">
                    {step.image && (
                      <div className="configuration-image-wrapper">
                        <div className="step-image configuration-image">
                          <img 
                            src={step.image} 
                            alt={`Step ${step.id}: ${step.title}`}
                            onClick={() => openLightbox(step.image, `Step ${step.id}: ${step.title}`)}
                            className="clickable-image"
                          />
                        </div>
                      </div>
                    )}
                    <div className="configuration-details">
                      <div className="configuration-section">
                        <h4 className="configuration-title">Configuration Options</h4>
                        {step.configurationInfo.projectName && (
                          <div className="configuration-item">
                            <div className="configuration-text">
                              <strong>Project Name</strong>
                              <p>{step.configurationInfo.projectName}</p>
                            </div>
                          </div>
                        )}
                        {step.configurationInfo.exposedPorts && (
                          <div className="configuration-item">
                            <div className="configuration-text">
                              <strong>Exposed Ports</strong>
                              <p>{step.configurationInfo.exposedPorts}</p>
                            </div>
                          </div>
                        )}
                        {step.configurationInfo.environmentVariables && (
                          <div className="configuration-item">
                            <div className="configuration-text">
                              <strong>Environment Variables</strong>
                              <p>{step.configurationInfo.environmentVariables}</p>
                            </div>
                          </div>
                        )}
                        {step.configurationInfo.resources && (
                          <div className="configuration-item">
                            <div className="configuration-text">
                              <strong>CPU & Memory Allocation</strong>
                              <p>{step.configurationInfo.resources}</p>
                            </div>
                          </div>
                        )}
                        <p>
                        For more information, see{' '}
                        <a href="https://aedify.ai/pricing" target="_blank" rel="noopener noreferrer" className="pricing-link">Aedify pricing</a>.
                      </p>
                        {step.configurationInfo.autoDeploy && (
                          <div className="configuration-item">
                            <div className="configuration-text">
                              <strong>Auto-Deploy</strong>
                              <p>{step.configurationInfo.autoDeploy}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {step.image && (
                      <div className="step-image">
                        <img 
                          src={step.image} 
                          alt={`Step ${step.id}: ${step.title}`}
                          onClick={() => openLightbox(step.image, `Step ${step.id}: ${step.title}`)}
                          className="clickable-image"
                        />
                      </div>
                    )}
                    <div className="step-details">
                      <p>{step.details}</p>
                    </div>
                  </>
                )}
              </div>
            ))}
          </section>

          {/* Shared Configuration and Deployment Steps */}
          <section className="deployment-section">
            <h2>Configure and Deploy</h2>
            <p className="section-intro">
              After selecting your repository or image, configure your project settings and deploy. 
              These steps are the same for both GitHub and Image deployments.
            </p>

            {sharedDeploymentSteps.map((step) => (
              <div 
                key={step.id} 
                className={`step-container ${activeStep === step.id + 30 ? 'active' : ''}`}
                id={`shared-step-${step.id}`}
              >
                <div className="step-header">
                  <div className="step-number">{step.id}</div>
                  <h3>{step.title}</h3>
                </div>
                <p className="step-description">{step.description}</p>
                {step.hasTwoOptions ? (
                  <div className="step-options">
                    {step.image && (
                      <div className="step-image">
                        <img 
                          src={step.image} 
                          alt={`Step ${step.id}: ${step.title}`}
                          onClick={() => openLightbox(step.image, `Step ${step.id}: ${step.title}`)}
                          className="clickable-image"
                        />
                      </div>
                    )}
                    <div className="step-details">
                      <p>{step.details}</p>
                    </div>
                    <div className="options-container">
                      <div className="option-card">
                        <div className="option-header">
                          <span className="option-label">Option A</span>
                          <h4>{step.optionA.title}</h4>
                        </div>
                        <p className="option-description">{step.optionA.description}</p>
                        <div className="option-details">
                          <p>{step.optionA.details}</p>
                        </div>
                      </div>
                      <div className="option-card">
                        <div className="option-header">
                          <span className="option-label">Option B</span>
                          <h4>{step.optionB.title}</h4>
                        </div>
                        <p className="option-description">{step.optionB.description}</p>
                        <div className="option-details">
                          <p>{step.optionB.details}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : step.hasDetectionInfo ? (
                  <div className="step-detection-content">
                    <div className="detection-details">
                      <div className="step-details">
                        <p>{step.details}</p>
                      </div>
                      <div className="detection-section">
                        <h4 className="detection-title">Automatic Detection</h4>
                        <div className="detection-item">
                          <div className="detection-text">
                            <strong>Framework Detection</strong>
                            <p>{step.detectionInfo.framework}</p>
                          </div>
                        </div>
                        <div className="detection-item">
                          <div className="detection-text">
                            <strong>Docker Detection</strong>
                            <p>{step.detectionInfo.docker}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    {step.image && (
                      <div className="detection-image-wrapper">
                        <div className="step-image detection-image">
                          <img 
                            src={step.image} 
                            alt={`Step ${step.id}: ${step.title}`}
                            onClick={() => openLightbox(step.image, `Step ${step.id}: ${step.title}`)}
                            className="clickable-image"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ) : step.hasConfigurationInfo ? (
                  <div className="step-configuration-content">
                    {step.image && (
                      <div className="configuration-image-wrapper">
                        <div className="step-image configuration-image">
                          <img 
                            src={step.image} 
                            alt={`Step ${step.id}: ${step.title}`}
                            onClick={() => openLightbox(step.image, `Step ${step.id}: ${step.title}`)}
                            className="clickable-image"
                          />
                        </div>
                      </div>
                    )}
                    <div className="configuration-details">
                      <div className="configuration-section">
                        <h4 className="configuration-title">Configuration Options</h4>
                        {step.configurationInfo.projectName && (
                          <div className="configuration-item">
                            <div className="configuration-text">
                              <strong>Project Name</strong>
                              <p>{step.configurationInfo.projectName}</p>
                            </div>
                          </div>
                        )}
                        {step.configurationInfo.exposedPorts && (
                          <div className="configuration-item">
                            <div className="configuration-text">
                              <strong>Exposed Ports</strong>
                              <p>{step.configurationInfo.exposedPorts}</p>
                            </div>
                          </div>
                        )}
                        {step.configurationInfo.environmentVariables && (
                          <div className="configuration-item">
                            <div className="configuration-text">
                              <strong>Environment Variables</strong>
                              <p>{step.configurationInfo.environmentVariables}</p>
                            </div>
                          </div>
                        )}
                        {step.configurationInfo.resources && (
                          <div className="configuration-item">
                            <div className="configuration-text">
                              <strong>CPU & Memory Allocation</strong>
                              <p>{step.configurationInfo.resources}</p>
                            </div>
                          </div>
                        )}
                        {step.configurationInfo.autoDeploy && (
                          <div className="configuration-item">
                            <div className="configuration-text">
                              <strong>Auto-Deploy</strong>
                              <p>{step.configurationInfo.autoDeploy}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {step.image && (
                      <div className="step-image">
                        <img 
                          src={step.image} 
                          alt={`Step ${step.id}: ${step.title}`}
                          onClick={() => openLightbox(step.image, `Step ${step.id}: ${step.title}`)}
                          className="clickable-image"
                        />
                      </div>
                    )}
                    <div className="step-details">
                      <p>{step.details}</p>
                    </div>
                  </>
                )}
              </div>
            ))}
          </section>

          {/* Additional Information */}
          <section className="info-section">
            <h2>App Management</h2>
            <div className="info-grid">
              <div className="info-card">
                <h3>Viewing Apps</h3>
                <p>
                  All your deployed applications are listed in the Apps section. You can see their status, 
                  resource usage, and access logs in real-time.
                </p>
              </div>
              <div className="info-card">
                <h3>Scaling Applications</h3>
                <p>
                  Scale your applications horizontally by adjusting the number of replicas. You can also 
                  scale vertically by modifying CPU and memory limits.
                </p>
              </div>
              <div className="info-card">
                <h3>Updating Applications</h3>
                <p>
                  For GitHub deployments, push changes to your repository and Aedify.AI will automatically 
                  rebuild and redeploy. For image deployments, update the image tag and redeploy.
                </p>
              </div>
            </div>
          </section>

          <section className="info-section">
            <h2>Additional Configuration</h2>
            <div className="info-grid">
              <div className="info-card">
                <h3>Domain Configuration</h3>
                <p>
                  Configure custom domains for your applications. Navigate to your app settings and add 
                  your domain. Aedify.AI provides automatic SSL certificate management.
                </p>
              </div>
              <div className="info-card">
                <h3>Environment Variables</h3>
                <p>
                  Manage environment variables for your applications. You can set variables at the project 
                  level or application level. Sensitive values are encrypted and stored securely.
                </p>
              </div>
              <div className="info-card">
                <h3>Resource Limits</h3>
                <p>
                  Configure CPU and memory limits for your applications. Resource limits help ensure fair 
                  resource allocation and prevent applications from consuming excessive resources.
                </p>
              </div>
            </div>
          </section>

          <PageNavigation />
        </div>

        {/* Right Sidebar - Step Navigation */}
        <aside className="apps-sidebar">
          <div className="sidebar-section">
            <h3>Deploy from GitHub</h3>
            <nav className="step-nav">
              {steps.map((step) => (
                <a
                  key={step.id}
                  href={`#github-step-${step.id}`}
                  className={`step-nav-item ${activeStep === step.id + 10 ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault()
                    setActiveStep(step.id + 10)
                    document.getElementById(`github-step-${step.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                >
                  <span className="step-nav-number">{step.id}</span>
                  <span className="step-nav-title">{step.title}</span>
                </a>
              ))}
            </nav>
          </div>

          <div className="sidebar-section">
            <h3>Deploy from Image</h3>
            <nav className="step-nav">
              {deployFromImageSteps.map((step) => (
                <a
                  key={step.id}
                  href={`#image-step-${step.id}`}
                  className={`step-nav-item ${activeStep === step.id + 20 ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault()
                    setActiveStep(step.id + 20)
                    document.getElementById(`image-step-${step.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                >
                  <span className="step-nav-number">{step.id}</span>
                  <span className="step-nav-title">{step.title}</span>
                </a>
              ))}
              {sharedDeploymentSteps.map((step) => (
                <a
                  key={step.id}
                  href={`#shared-step-${step.id}`}
                  className={`step-nav-item ${activeStep === step.id + 30 ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault()
                    setActiveStep(step.id + 30)
                    document.getElementById(`shared-step-${step.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                >
                  <span className="step-nav-number">{step.id}</span>
                  <span className="step-nav-title">{step.title}</span>
                </a>
              ))}
            </nav>
          </div>

          <div className="sidebar-section tips-section">
            <h3>Tips</h3>
            <ul className="tips-list">
              <li>Use external links for quick deployments of public repos</li>
              <li>Connect your GitHub account for private repos and auto-deploy</li>
              <li>Make sure your repository has a valid build configuration</li>
              <li>Check your environment variables before deploying</li>
              <li>Monitor deployment logs for any build errors</li>
              <li>Start with minimal resources and scale up as needed</li>
            </ul>
          </div>
        </aside>
      </div>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox} aria-label="Close lightbox">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <img src={lightboxImage} alt={lightboxAlt} className="lightbox-image" />
          </div>
        </div>
      )}
    </div>
  )
}

export default Apps
